import React from 'react';
import { EditPageContainer, Page, withServerErrors } from 'components/common';
import { FormGroup, SaveButton, BackButton } from 'components/common/Forms';
import { compose, withState, withHandlers } from 'recompose';
import { LocalForm, Fieldset, Control } from 'react-redux-form';
import { objectToIndexedFormData } from 'services';
import { TabContent, TabPane, Nav, NavItem, NavLink } from 'reactstrap';
import classnames from 'classnames';
import { omit } from 'lodash';
import Users  from 'pages/Users';
import withLifecycle from '@hocs/with-lifecycle';

import SLA from './SLA';
import UserDetails from './UserDetails';
import InstallerDetails from './InstallerDetails';
import CountriesAndRegions from './CountriesAndRegions';
import InstallerPriority from './InstallerPriority';
import InstallerDevices from './InstallerDevices';
import InstallerFeatures from './InstallerFeatures';
import InstallerSurrogateID from './InstallerSurrogateID';
import LogoUpload from './LogoUpload';
import InstallerIssuers from './InstallerIssuers';

const passwordsMatch = ({ password, confirmPassword }) =>
  password === confirmPassword;

const tabs = [
  'Details',
  'Users',
  'Capabilities',
  'Countries and Regions',
  'Region Priority',
];

export const InstallerEdit = ({
  mode,
  item,
  form,
  onBack,
  activeTab,
  toggleTab,
  setDispatch,
  dispatch,
  onSubmit,
  onUpdate,
  onChange,
  logo,
  onFileDrop,
  devices,
  renderServerError,
  readOnly,
}) => (
  <Page title={`${mode} Installer`}>
    <Nav pills>
      {tabs.map((t, i) => (
        <NavItem key={i}>
          <NavLink
            className={classnames({ active: activeTab === i })}
            onClick={e => toggleTab(e, i)}
            href="#"
          >
            {t}
          </NavLink>
        </NavItem>
      ))}
    </Nav>
    <br />
    <LocalForm
      initialState={item}
      onSubmit={onSubmit}
      getDispatch={setDispatch}
      onUpdate={onUpdate}
      onChange={onChange}
    >
      <TabContent activeTab={activeTab}>
        <TabPane tabId={tabs.indexOf('Details')}>
          <FormGroup label="Installer Name *" model=".name">
            <Control.text
              updateOn="blur"
              model=".name"
              required
              disabled={readOnly}
            />
          </FormGroup>
          <LogoUpload
            item={item}
            logo={logo}
            onFileDrop={onFileDrop}
            readOnly={readOnly}
            renderServerError={renderServerError}
          />
          <hr />
          <h4>Contact Details</h4>
          <InstallerDetails
            item={item}
            dispatch={dispatch}
            readOnly={readOnly}
            renderServerError={renderServerError}
          />
        </TabPane>
        <TabPane tabId={tabs.indexOf('Capabilities')}>
          <InstallerDevices dispatch={dispatch} item={item} readOnly={readOnly} />
          <InstallerFeatures dispatch={dispatch} item={item} readOnly={readOnly} />
          <InstallerIssuers dispatch={dispatch} item={item} is_all_issuers={item.is_all_issuers} readOnly={readOnly} />
          <InstallerSurrogateID dispatch={dispatch} item={item} readOnly={readOnly} />
          <FormGroup label="Max Jobs Per Order (0 equals no limit)" model=".max_jobs">
            <Control.text
                type="number"
                updateOn="blur"
                model=".max_jobs"
                disabled={readOnly}
            />
          </FormGroup>
          <FormGroup label="Max Active Jobs (0 equals no limit)" model=".max_active_jobs">
            <Control.text
                type="number"
                updateOn="blur"
                model=".max_active_jobs"
                disabled={readOnly}
            />
          </FormGroup>
          {/*<FormGroup label="Project Orders" check model=".project_orders">*/}
          {/*  <Control.checkbox*/}
          {/*      model=".project_orders"*/}
          {/*      disabled={readOnly}*/}
          {/*  />*/}
          {/*</FormGroup>*/}
        </TabPane>
        <TabPane tabId={tabs.indexOf('Countries and Regions')}>
          <CountriesAndRegions
            readOnly={readOnly}
            item={item}
            dispatch={dispatch}
          />
        </TabPane>
        {item &&
          item.id && (
            <TabPane tabId={tabs.indexOf('Region Priority')}>
              <InstallerPriority readOnly={readOnly} installerId={item.id} />
            </TabPane>
          )}
        <TabPane tabId={tabs.indexOf('SLA')}>
          <SLA readOnly={readOnly} />
        </TabPane>
        <TabPane tabId={tabs.indexOf('Users')}>
          <Users.List
            url={`installer-users?installer=${item.id}`}
            deleteUrl={`installer-users`}
            match={{url: `${item.id}/users`}}
          />
        </TabPane>
      </TabContent>
      <div className='d-flex'>
        <div className='ml-auto'/>
        <BackButton onClick={onBack} />
        {activeTab !== tabs.indexOf('Region Priority') && (
          <SaveButton className="ml-2 mr-2" form={form} hidden={readOnly} />
        )}
      </div>
    </LocalForm>
  </Page>
);

const enhance = compose(
  withServerErrors(),
  withState('activeTab', 'setActiveTab', tabs.indexOf('Details')),
  withState('logo', 'setLogo', null),
  EditPageContainer({
    url: 'installers',
    defaultItem: {
      devices: [],
      billing_address_same: false,
    },
    afterFetched: () => item => {
      item.devices = item.devices.map(d => { return {...d, value: d.device_name}});
      item.features = item.features.map(f => { return {...f, value: f.value}});
      item.issuers = item.issuers.map(i => { return {...i, value: i.description}});
      item.is_all_issuers = item.is_all_issuers;
      item['billing_address_same'] =
        item.billing_address.id === item.contact_address.id;

      return item;
    },
    beforeSave: ({ logo }) => item => {
      const obj = Object.assign({}, item);

      if (obj.billing_address_same) {
        obj['billing_address'] = obj['contact_address'];
      }

      if (logo) {
        const data = objectToIndexedFormData(obj);
        data.append('logo_image_file', logo);
        return data;
      }

      //obj.devices = obj.devices.split(",");

      return obj;
    },
  }),
  withState('serverErrors', 'setServerErrors', {}),
  withHandlers({
    onFileDrop: ({ setLogo }) => accepted => {
      setLogo(accepted[0]);
    },
    toggleTab: ({ setActiveTab }) => (event, tab) => {
      event.preventDefault();
      setActiveTab(tab);
    },
  }),
  withLifecycle({
    onDidMount: ({ setActiveTab }) => {
      const index = tabs.indexOf(
        decodeURIComponent(window.location.hash.replace('#', ''))
      );
      index >= 0 && setActiveTab(index);
    },
  })
);

export default enhance(InstallerEdit);



// WEBPACK FOOTER //
// ./src/pages/Installers/Edit.jsx